import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Cookie, Mail, ToggleLeft, ToggleRight } from 'lucide-react';
import { CONSENT_KEY, grantConsent, denyConsent } from '../analytics';
import './Legal.css';
import './Cookies.css';

const SECTIONS_PT = [
  {
    title: '1. O que são cookies',
    content: 'Cookies são pequenos arquivos de texto armazenados no seu navegador quando você visita um site. Eles permitem que o site reconheça seu dispositivo em visitas futuras e melhore sua experiência.'
  },
  {
    title: '2. Cookies que utilizamos',
    content: 'Utilizamos apenas dois tipos de cookies: (1) Cookies essenciais — necessários para o funcionamento básico do site, como manter sua sessão ativa. Não podem ser desativados. (2) Cookies analíticos (Google Analytics 4) — coletamos dados anonimizados sobre como você usa o site (páginas visitadas, tempo de permanência, origem do acesso) para melhorar nosso conteúdo. Só são ativados com seu consentimento.'
  },
  {
    title: '3. Google Analytics 4',
    content: 'Utilizamos o Google Analytics 4 (GA4) para análise de tráfego. O GA4 opera com Consent Mode v2 — antes do seu consentimento, nenhum dado identificável é coletado. Após aceitar, dados anonimizados são enviados para servidores do Google nos EUA, cobertos pelo Data Privacy Framework EU-EUA. Você pode consultar a política de privacidade do Google em policies.google.com/privacy.'
  },
  {
    title: '4. Cookies de terceiros',
    content: 'O site utiliza o widget de chat Libben (próprio) que não coloca cookies de terceiros. Não utilizamos pixels do Meta, LinkedIn, TikTok ou qualquer outra rede social.'
  },
  {
    title: '5. Como gerenciar seus cookies',
    content: 'Você pode aceitar ou recusar cookies analíticos a qualquer momento usando o painel abaixo. Também pode limpar cookies existentes nas configurações do seu navegador: Chrome → Configurações → Privacidade → Limpar dados de navegação. Firefox → Configurações → Privacidade → Limpar dados. Safari → Preferências → Privacidade → Gerenciar dados do site.'
  },
  {
    title: '6. Retenção',
    content: 'Cookies analíticos do GA4 expiram em 2 anos. Cookies essenciais de sessão expiram ao fechar o navegador. Sua preferência de consentimento é armazenada por 12 meses no localStorage do seu navegador.'
  },
  {
    title: '7. Contato',
    content: 'Para dúvidas sobre nossa política de cookies, entre em contato pelo e-mail privacidade@libben.com.br.'
  }
];

const SECTIONS_EN = [
  {
    title: '1. What are cookies',
    content: 'Cookies are small text files stored in your browser when you visit a website. They allow the site to recognize your device on future visits and improve your experience.'
  },
  {
    title: '2. Cookies we use',
    content: 'We use only two types of cookies: (1) Essential cookies — necessary for basic site functionality, such as maintaining your session. Cannot be disabled. (2) Analytics cookies (Google Analytics 4) — we collect anonymized data about how you use the site (pages visited, time spent, traffic source) to improve our content. Only activated with your consent.'
  },
  {
    title: '3. Google Analytics 4',
    content: 'We use Google Analytics 4 (GA4) for traffic analysis. GA4 operates with Consent Mode v2 — before your consent, no identifiable data is collected. After accepting, anonymized data is sent to Google servers in the US, covered by the EU-US Data Privacy Framework. You can review Google\'s privacy policy at policies.google.com/privacy.'
  },
  {
    title: '4. Third-party cookies',
    content: 'The site uses the Libben chat widget (proprietary) which does not place third-party cookies. We do not use Meta, LinkedIn, TikTok or any other social media pixels.'
  },
  {
    title: '5. Managing your cookies',
    content: 'You can accept or refuse analytics cookies at any time using the panel below. You can also clear existing cookies in your browser settings: Chrome → Settings → Privacy → Clear browsing data. Firefox → Settings → Privacy → Clear data. Safari → Preferences → Privacy → Manage website data.'
  },
  {
    title: '6. Retention',
    content: 'GA4 analytics cookies expire after 2 years. Essential session cookies expire when you close your browser. Your consent preference is stored for 12 months in your browser\'s localStorage.'
  },
  {
    title: '7. Contact',
    content: 'For questions about our cookie policy, contact us at privacy@libben.com.br.'
  }
];

export default function Cookies() {
  const { i18n } = useTranslation();
  const isPt = i18n.language?.startsWith('pt');
  const sections = isPt ? SECTIONS_PT : SECTIONS_EN;

  const [consent, setConsent] = useState(() => localStorage.getItem(CONSENT_KEY) || 'pending');

  const handleAccept = () => { grantConsent(); setConsent('accepted'); };
  const handleDeny  = () => { denyConsent();  setConsent('denied'); };

  return (
    <div className="legal-page">
      <Helmet>
        <title>{isPt ? 'Política de Cookies — Libben Agents' : 'Cookie Policy — Libben Agents'}</title>
        <meta name="description" content={isPt ? 'Política de Cookies da Libben Agents. Saiba quais cookies usamos e como gerenciá-los.' : 'Libben Agents Cookie Policy. Learn which cookies we use and how to manage them.'} />
        <link rel="canonical" href="https://libbenagents.com/cookies" />
      </Helmet>

      <div className="legal-glow" />
      <div className="legal-container">
        <div className="legal-header">
          <Cookie size={28} className="legal-icon" />
          <h1 className="legal-title">{isPt ? 'Política de Cookies' : 'Cookie Policy'}</h1>
          <div className="legal-meta">
            <span className="legal-badge">{isPt ? 'Versão 1.0' : 'Version 1.0'}</span>
            <span className="legal-date">{isPt ? 'Última atualização: 01 de julho de 2026' : 'Last updated: July 1, 2026'}</span>
          </div>
        </div>

        {/* Painel de consentimento */}
        <div className="consent-panel">
          <div className="consent-panel-header">
            <span className="consent-panel-title">{isPt ? 'Suas preferências de cookies' : 'Your cookie preferences'}</span>
            <span className={`consent-status consent-status--${consent}`}>
              {consent === 'accepted' ? (isPt ? '✓ Aceito' : '✓ Accepted')
               : consent === 'denied' ? (isPt ? '✕ Recusado' : '✕ Declined')
               : (isPt ? 'Pendente' : 'Pending')}
            </span>
          </div>

          <div className="consent-row">
            <div className="consent-row-info">
              <strong>{isPt ? 'Cookies essenciais' : 'Essential cookies'}</strong>
              <span>{isPt ? 'Necessários para o funcionamento do site' : 'Required for site functionality'}</span>
            </div>
            <ToggleRight size={28} className="consent-toggle consent-toggle--on" />
          </div>

          <div className="consent-row">
            <div className="consent-row-info">
              <strong>{isPt ? 'Cookies analíticos (GA4)' : 'Analytics cookies (GA4)'}</strong>
              <span>{isPt ? 'Dados anonimizados para melhoria do site' : 'Anonymized data to improve the site'}</span>
            </div>
            {consent === 'accepted'
              ? <ToggleRight size={28} className="consent-toggle consent-toggle--on" onClick={handleDeny} style={{ cursor: 'pointer' }} />
              : <ToggleLeft  size={28} className="consent-toggle consent-toggle--off" onClick={handleAccept} style={{ cursor: 'pointer' }} />
            }
          </div>

          <div className="consent-actions">
            <button className="consent-btn consent-btn--deny" onClick={handleDeny}>
              {isPt ? 'Recusar analíticos' : 'Decline analytics'}
            </button>
            <button className="consent-btn consent-btn--accept" onClick={handleAccept}>
              {isPt ? 'Aceitar analíticos' : 'Accept analytics'}
            </button>
          </div>
        </div>

        <div className="legal-sections">
          {sections.map((section, i) => (
            <div key={i} className="legal-section">
              <h2 className="legal-section-title">{section.title}</h2>
              <p className="legal-section-text">{section.content}</p>
            </div>
          ))}
        </div>

        <div className="legal-contact">
          <h3 className="legal-contact-title">{isPt ? 'Contato' : 'Contact'}</h3>
          <div className="legal-contact-grid">
            <a href="mailto:privacidade@libben.com.br" className="legal-contact-item">
              <Mail size={14} />
              <span>{isPt ? 'Privacidade / DPO' : 'Privacy / DPO'}</span>
              <strong>privacidade@libben.com.br</strong>
            </a>
          </div>
        </div>

        <p className="legal-footer">
          {isPt
            ? '© 2026 FGMAX - INTERMEDIACAO DE NEGOCIOS EMPRESARIAIS E INFORMATICA LTDA — CNPJ 22.940.511/0001-26. Todos os direitos reservados.'
            : '© 2026 FGMAX - INTERMEDIACAO DE NEGOCIOS EMPRESARIAIS E INFORMATICA LTDA — CNPJ 22.940.511/0001-26. All rights reserved.'
          }
        </p>
      </div>
    </div>
  );
}
